import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Tag, SearchResult, SearchResponse } from '@/types';

// List tags
export function useTags() {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async (): Promise<Tag[]> => {
      const response = await api.get<ApiResponse<Tag[]>>('/tags');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch tags');
      }
      return response.data.data;
    },
  });
}

// Create tag
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string): Promise<Tag> => {
      const response = await api.post<ApiResponse<Tag>>('/tags', { name });
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to create tag');
      }
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

// Delete tag
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete<ApiResponse>(`/tags/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete tag');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

// Get pages by tag
export function useTagPages(tagName: string, params: { page?: number; perPage?: number } = {}) {
  return useQuery({
    queryKey: ['tagPages', tagName, params],
    queryFn: async (): Promise<PaginatedData<SearchResult>> => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.perPage) searchParams.set('perPage', String(params.perPage));

      const response = await api.get<ApiResponse<PaginatedData<SearchResult>>>(`/tags/${encodeURIComponent(tagName)}/pages?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch tag pages');
      }
      return response.data.data;
    },
    enabled: !!tagName,
  });
}

// Search pages
export function useSearch(query: string, params: { page?: number; perPage?: number } = {}) {
  return useQuery({
    queryKey: ['search', query, params],
    queryFn: async (): Promise<SearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('q', query);
      if (params.page) searchParams.set('page', String(params.page));
      if (params.perPage) searchParams.set('perPage', String(params.perPage));

      const response = await api.get<ApiResponse<SearchResponse>>(`/search?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Search failed');
      }
      return response.data.data;
    },
    enabled: query.length > 0,
  });
}
